import { useState } from 'react';
import { 
  CheckCircle2, 
  Activity, 
  Database, 
  Cpu, 
  Search, 
  Mic, 
  ShieldCheck, 
  RefreshCw,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { SYSTEM_HEALTH_MATRIX } from '../executive-data.js';
import { Badge } from '../../../components/ui/Badge.jsx';
import { Button } from '../../../components/ui/Button.jsx';
import { cn } from '../../../lib/utils.js';

const ICON_MAP = {
  d1: Database,
  vectorize: Cpu,
  'ai-search': Search,
  'voice-webrtc': Mic,
  'tenant-isolation': ShieldCheck,
};

export function SystemHealthStatus({ 
  systems = SYSTEM_HEALTH_MATRIX,
  onRefresh,
  className
}) {
  const [expanded, setExpanded] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const allHealthy = systems.every(s => s.status === 'HEALTHY' || s.status === 'operational');
  const avgLatency = Math.round(
    systems.reduce((acc, curr) => acc + (curr.latencyMs || 0), 0) / (systems.length || 1)
  );

  const handleRefresh = async () => {
    setRefreshing(true);
    if (onRefresh) {
      await onRefresh();
    }
    setTimeout(() => setRefreshing(false), 400);
  };

  return (
    <div 
      data-testid="system-health-status"
      className={cn(
        'rounded-2xl border border-slate-200 bg-white p-5 shadow-xs transition-all',
        className
      )}
    >
      {/* Header Summary Row */}
      <div className="flex flex-wrap items-center justify-between gap-4 pb-4 border-b border-slate-200">
        <div className="flex items-center gap-3">
          <div className="relative flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700 border border-emerald-200">
            <Activity className="h-5 w-5" />
            <span className="absolute -top-1 -right-1 flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-600"></span>
            </span>
          </div>

          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-display text-sm font-bold text-slate-900">
                Plattform &amp; Infrastruktur Status
              </h3>
              <Badge 
                variant="outline"
                className="bg-slate-100 text-slate-700 border-slate-300 text-[11px] font-semibold py-0.5 px-2"
              >
                {allHealthy ? 'Alle Systeme betriebsbereit' : 'Demo / Vorschau-Status'}
              </Badge>
            </div>
            <p className="text-xs text-slate-500 mt-0.5">
              5 Kernsubsysteme • Vorschau-Modus (Keine Live-Telemetrie im Shop-Release)
            </p>
          </div>
        </div>

        {/* Action Controls */}
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
            className="h-8 px-2.5 text-xs text-slate-600 hover:text-[var(--primary)] gap-1.5 cursor-pointer focus-visible:ring-2 focus-visible:ring-[var(--primary)]"
            aria-label="Systemstatus aktualisieren"
          >
            <RefreshCw className={cn('h-3.5 w-3.5', refreshing && 'animate-spin text-[var(--primary)]')} />
            <span className="hidden sm:inline">Prüfen</span>
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={() => setExpanded(!expanded)}
            className="h-8 px-2.5 text-xs font-semibold gap-1 cursor-pointer border-slate-300 focus-visible:ring-2 focus-visible:ring-[var(--primary)]"
            aria-expanded={expanded}
          >
            <span>{expanded ? 'Kompakt' : 'Details'}</span>
            {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          </Button>
        </div>
      </div>

      {/* Grid of Subsystems */}
      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
        {systems.map((sys) => {
          const Icon = ICON_MAP[sys.id] || Activity;
          const isHealthy = sys.status === 'HEALTHY' || sys.status === 'operational';

          return (
            <div
              key={sys.id}
              data-testid={`system-health-card-${sys.id}`}
              className="group relative flex flex-col justify-between rounded-xl border border-slate-200 bg-slate-50/70 p-3.5 transition-all hover:bg-white hover:border-[var(--primary)] hover:shadow-xs"
            >
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-white shadow-2xs border border-slate-200 text-[var(--primary,#e3000b)]">
                    <Icon className="h-3.5 w-3.5" />
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className={cn(
                      'h-2 w-2 rounded-full',
                      isHealthy ? 'bg-emerald-600' : 'bg-amber-600'
                    )} />
                    <span className="font-mono tabular-nums text-[10px] font-bold text-slate-600">
                      {sys.latencyMs}ms
                    </span>
                  </div>
                </div>

                <h4 className="font-display text-xs font-bold text-slate-900 leading-tight">
                  {sys.name}
                </h4>
                <p className="text-[11px] font-medium text-slate-500 mt-0.5 truncate" title={sys.subsystem}>
                  {sys.subsystem}
                </p>
              </div>

              {expanded && (
                <div className="mt-2.5 pt-2 border-t border-slate-200 text-[10px] text-slate-600 space-y-1 animate-fadeIn">
                  <p className="font-mono tabular-nums text-[10px] text-slate-800 bg-white p-1 rounded border border-slate-200">
                    {sys.metrics}
                  </p>
                  <p className="leading-snug text-slate-600">
                    {sys.description}
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
